import { supabase } from "~/integrations/supabase/client";

// New parallel inventory module — operations (inv_* RPCs). Not in generated types yet.
export type InvPlant = "P1" | "P2" | "NC";
export type InvOpType = "reception" | "delivery" | "transfer" | "adjustment" | "scrap";

export interface InvProductOption {
  id: string;
  code: string | null;
  name: string;
  stock_uom: string;
  source_kind: "finished_good" | "raw_material" | "oil";
}

export interface InvMovementRow {
  move_id: string;
  fecha: string;
  documento: string | null;
  tipo: string | null;
  code: string | null;
  producto: string;
  uom: string | null;
  origen: string | null;
  destino: string | null;
  cantidad: number;
  valor: number | null;
  estado: string;
  usuario: string | null;
}

export interface InvKardexRow {
  fecha: string;
  documento: string | null;
  tipo: string | null;
  code: string | null;
  producto: string;
  ubicacion: string;
  uom: string | null;
  entrada: number;
  salida: number;
  saldo: number;
  estado: string;
  // Originating purchase order for reception moves (null for other move types).
  purchase_order_id: string | null;
  // Originating production batch for mfg_in / mfg_out moves (null for other move types).
  production_batch_id: string | null;
}

export interface InvPendingPicking {
  picking_id: string;
  name: string;
  op_code: string;
  tipo: string;
  code: string | null;
  producto: string | null;
  cantidad: number | null;
  uom: string | null;
  origen: string | null;
  destino: string | null;
  created_by_name: string | null;
  created_at: string;
  reference: string | null;
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  if (error) throw error;
  return data as unknown as T;
}

export const inventoryOperationsService = {
  searchProducts: (search?: string) =>
    rpc<InvProductOption[]>("search_inv_products", {
      p_search: search?.trim() || null,
      p_limit: 1000,
    }),

  getMovements: (search?: string) =>
    rpc<InvMovementRow[]>("get_inv_movements", {
      p_search: search?.trim() || null,
      p_limit: 200,
    }),

  getPending: () => rpc<InvPendingPicking[]>("get_inv_pending_pickings", {}),

  getKardex: (opts: {
    search?: string;
    locationCode?: string | null;
    productId?: string | null;
  }) =>
    rpc<InvKardexRow[]>("get_inv_kardex", {
      p_product_id: opts.productId || null,
      p_search: opts.search?.trim() || null,
      p_location_code: opts.locationCode || null,
    }),

  createReception: (p: {
    productId: string;
    plant: InvPlant;
    quantity: number;
    uom?: string;
    priceUnit?: number | null;
    lotName?: string | null;
    reference?: string | null;
    createdBy?: string | null;
  }) =>
    rpc<string>("inv_create_reception", {
      p_product_id: p.productId,
      p_plant: p.plant,
      p_quantity: p.quantity,
      p_uom: p.uom ?? null,
      p_price_unit: p.priceUnit ?? null,
      p_lot_name: p.lotName ?? null,
      p_reference: p.reference ?? null,
      p_created_by: p.createdBy ?? null,
    }),

  createDelivery: (p: {
    productId: string;
    plant: InvPlant;
    quantity: number;
    uom?: string;
    reference?: string | null;
    createdBy?: string | null;
  }) =>
    rpc<string>("inv_create_delivery", {
      p_product_id: p.productId,
      p_plant: p.plant,
      p_quantity: p.quantity,
      p_uom: p.uom ?? null,
      p_reference: p.reference ?? null,
      p_created_by: p.createdBy ?? null,
    }),

  createTransfer: (p: {
    productId: string;
    fromPlant: InvPlant;
    toPlant: InvPlant;
    quantity: number;
    uom?: string;
    reference?: string | null;
    createdBy?: string | null;
  }) =>
    rpc<string>("inv_create_transfer", {
      p_product_id: p.productId,
      p_from_plant: p.fromPlant,
      p_to_plant: p.toPlant,
      p_quantity: p.quantity,
      p_uom: p.uom ?? null,
      p_reference: p.reference ?? null,
      p_created_by: p.createdBy ?? null,
    }),

  createAdjustment: (p: {
    productId: string;
    plant: InvPlant;
    direction: "in" | "out";
    quantity: number;
    uom?: string;
    priceUnit?: number | null;
    reason?: string | null;
    createdBy?: string | null;
  }) =>
    rpc<string>("inv_create_adjustment", {
      p_product_id: p.productId,
      p_plant: p.plant,
      p_direction: p.direction,
      p_quantity: p.quantity,
      p_uom: p.uom ?? null,
      p_price_unit: p.priceUnit ?? null,
      p_reason: p.reason ?? null,
      p_created_by: p.createdBy ?? null,
    }),

  createScrap: (p: {
    productId: string;
    plant: InvPlant;
    quantity: number;
    uom?: string;
    reason?: string | null;
    createdBy?: string | null;
  }) =>
    rpc<string>("inv_create_scrap", {
      p_product_id: p.productId,
      p_plant: p.plant,
      p_quantity: p.quantity,
      p_uom: p.uom ?? null,
      p_reason: p.reason ?? null,
      p_created_by: p.createdBy ?? null,
    }),

  approve: (pickingId: string, userId?: string | null) =>
    rpc<void>("inv_approve_picking", {
      p_picking_id: pickingId,
      p_approved_by: userId ?? null,
    }),

  reject: (pickingId: string, userId?: string | null, reason?: string | null) =>
    rpc<void>("inv_reject_picking", {
      p_picking_id: pickingId,
      p_user: userId ?? null,
      p_reason: reason ?? null,
    }),
};

export const INV_PLANTS: { value: InvPlant; label: string }[] = [
  { value: "P1", label: "Planta 1" },
  { value: "P2", label: "Planta 2" },
  { value: "NC", label: "No Conforme" },
];

export const INV_OP_LABELS: Record<InvOpType, string> = {
  reception: "Recepción",
  delivery: "Entrega",
  transfer: "Traslado interno",
  adjustment: "Ajuste de inventario",
  scrap: "Desecho",
};

// Operations that require a second user to approve (draft → posted).
export const INV_OP_NEEDS_APPROVAL: Record<InvOpType, boolean> = {
  reception: false,
  delivery: false,
  transfer: true,
  adjustment: true,
  scrap: true,
};
